import axios from 'axios';

// Create axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5001/api',
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 30000 // 30 seconds
});

// Request interceptor - Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors globally
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle 401 Unauthorized - redirect to login
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }

    // Handle other errors
    const errorMessage = error.response?.data?.message || error.message || 'An error occurred';

    return Promise.reject({
      message: errorMessage,
      status: error.response?.status,
      data: error.response?.data
    });
  }
);

// Helper function to set auth token
export function setAuthToken(token) {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('token', token);
  } else {
    delete api.defaults.headers.common['Authorization'];
    localStorage.removeItem('token');
  }
}

// Helper function to get auth token
export function getAuthToken() {
  return localStorage.getItem('token');
}

// ============================================
// PHASE 4 API FUNCTIONS
// ============================================

// Quotation APIs
export const quotationAPI = {
  create: (projectId, items) => api.post('/quotations', { projectId, items }),
  get: (projectId, version = null) =>
    api.get(`/quotations/project/${projectId}`, { params: { version } }),
  getVersions: (projectId) =>
    api.get(`/quotations/project/${projectId}/versions`),
  getSummary: (projectId, version = null) =>
    api.get(`/quotations/project/${projectId}/summary`, { params: { version } }),
  finalize: (projectId, notes = null) =>
    api.post(`/quotations/${projectId}/finalize`, { notes }),
  updateItem: (itemId, status, clientNotes) =>
    api.patch(`/quotations/items/${itemId}`, { status, clientNotes }),
  getMyTasks: () =>
    api.get('/quotations/my-tasks'),
  start: (projectId) =>
    api.post(`/quotations/${projectId}/start`)
};

// Contract APIs
export const contractAPI = {
  generate: (projectId, templateId = null, customVariables = {}) =>
    api.post('/contracts/generate', { projectId, templateId, customVariables }),
  getTemplates: (templateType = null) =>
    api.get('/contracts/templates', { params: { templateType } }),
  getDefaultTemplate: (templateType = 'standard') =>
    api.get('/contracts/templates/default', { params: { templateType } }),
  createTemplate: (templateData) =>
    api.post('/contracts/templates', templateData),
  updateTemplate: (id, updates) =>
    api.patch(`/contracts/templates/${id}`, updates),
  approveTemplate: (id) =>
    api.post(`/contracts/templates/${id}/approve`),
  deactivateTemplate: (id) =>
    api.post(`/contracts/templates/${id}/deactivate`),
  setDefaultTemplate: (id) =>
    api.post(`/contracts/templates/${id}/set-default`)
};

// Signing Appointment APIs
export const signingAPI = {
  schedule: (appointmentData) =>
    api.post('/signing-appointments', appointmentData),
  getUpcoming: (limit = 10) =>
    api.get('/signing-appointments/upcoming', { params: { limit } }),
  getByProject: (projectId) =>
    api.get(`/signing-appointments/project/${projectId}`),
  getById: (id) =>
    api.get(`/signing-appointments/${id}`),
  confirmAttendance: (id) =>
    api.patch(`/signing-appointments/${id}/confirm`),
  recordSigned: (id, outcomeData) =>
    api.post(`/signing-appointments/${id}/signed`, outcomeData),
  recordBlocker: (id, blockerData) =>
    api.post(`/signing-appointments/${id}/blocker`, blockerData),
  resolveBlocker: (id, resolutionNotes) =>
    api.post(`/signing-appointments/${id}/resolve-blocker`, { resolutionNotes }),
  recordDeclined: (id, reason) =>
    api.post(`/signing-appointments/${id}/declined`, { reason })
};

// Measurement Appointment APIs
export const measurementAPI = {
  schedule: (appointmentData) =>
    api.post('/measurements', appointmentData),
  getAll: (filters = {}) =>
    api.get('/measurements', { params: filters }),
  getMy: (filters = {}) =>
    api.get('/measurements/my', { params: filters }),
  assign: (id, draughterIds) =>
    api.patch(`/measurements/${id}/assign`, { draughterIds: Array.isArray(draughterIds) ? draughterIds : [draughterIds] }),
  start: (id) =>
    api.post(`/measurements/${id}/start`),
  complete: (id, completionNotes) =>
    api.post(`/measurements/${id}/complete`, { completionNotes }),
  cancel: (id, reason) =>
    api.post(`/measurements/${id}/cancel`, { reason }),
  reschedule: (id, scheduledDate) =>
    api.patch(`/measurements/${id}/reschedule`, { scheduledDate }),
  setSchedule: (id, scheduledDate) =>
    api.patch(`/measurements/${id}/set-schedule`, { scheduledDate })
};

// 2D Drawing APIs
export const drawing2DAPI = {
  getMyTasks: () =>
    api.get('/drawing-2d/my-tasks'),
  start: (projectId) =>
    api.post(`/drawing-2d/${projectId}/start`),
  submit: (projectId, notes = '') =>
    api.post(`/drawing-2d/${projectId}/submit`, { notes }),
  getLastSubmissionDate: (projectId) =>
    api.get(`/drawing-2d/${projectId}/last-submission`)
};

// 3D Drawing APIs
export const drawing3DAPI = {
  getMyTasks: () =>
    api.get('/drawing-3d/my-tasks'),
  start: (projectId) =>
    api.post(`/drawing-3d/${projectId}/start`),
  submit: (projectId, notes = '') =>
    api.post(`/drawing-3d/${projectId}/submit`, { notes }),
  getLastSubmissionDate: (projectId) =>
    api.get(`/drawing-3d/${projectId}/last-submission`)
};

// DD (Detailed Design) APIs
export const ddAPI = {
  getMyTasks: () =>
    api.get('/dd/my-tasks'),
  start: (projectId) =>
    api.post(`/dd/${projectId}/start`),
  submit: (projectId, notes = '') =>
    api.post(`/dd/${projectId}/submit`, { notes })
};

// Project Files APIs
export const projectFilesAPI = {
  upload: (formData) =>
    api.post('/project-files/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  getByProject: (projectId) =>
    api.get(`/project-files/project/${projectId}`),
  download: (fileId) =>
    api.get(`/project-files/${fileId}/download`, {
      responseType: 'blob'
    }),
  delete: (fileId) =>
    api.delete(`/project-files/${fileId}`)
};

// Payment Milestone APIs
export const paymentMilestoneAPI = {
  // Get all milestones for a project
  getMilestones: (projectId) =>
    api.get(`/projects/${projectId}/payment-milestones`),

  // Get payment summary for a project
  getSummary: (projectId) =>
    api.get(`/projects/${projectId}/payment-summary`),

  // Initialize default milestones
  initialize: (projectId) =>
    api.post(`/projects/${projectId}/payment-milestones/initialize`),

  // Create a new milestone
  create: (projectId, data) =>
    api.post(`/projects/${projectId}/payment-milestones`, data),

  // Update a milestone
  update: (projectId, milestoneId, data) =>
    api.patch(`/projects/${projectId}/payment-milestones/${milestoneId}`, data),

  // Delete a milestone
  delete: (projectId, milestoneId) =>
    api.delete(`/projects/${projectId}/payment-milestones/${milestoneId}`),

  // Reorder milestones
  reorder: (projectId, order) =>
    api.put(`/projects/${projectId}/payment-milestones/reorder`, { order }),

  // Record a payment
  recordPayment: (projectId, milestoneId, data) =>
    api.post(`/projects/${projectId}/payment-milestones/${milestoneId}/payments`, data),

  // Update a payment
  updatePayment: (projectId, milestoneId, paymentId, data) =>
    api.patch(`/projects/${projectId}/payment-milestones/${milestoneId}/payments/${paymentId}`, data),

  // Delete a payment
  deletePayment: (projectId, milestoneId, paymentId) =>
    api.delete(`/projects/${projectId}/payment-milestones/${milestoneId}/payments/${paymentId}`),

  // Get milestone templates
  getTemplates: () =>
    api.get('/projects/payment-milestone-templates')
};

// Variation Order APIs
export const variationOrderAPI = {
  // Get all VOs for a project
  getVOs: (projectId) =>
    api.get(`/projects/${projectId}/variation-orders`),

  // Get VO summary for a project
  getSummary: (projectId) =>
    api.get(`/projects/${projectId}/variation-orders/summary`),

  // Create a new VO
  create: (projectId, data) =>
    api.post(`/projects/${projectId}/variation-orders`, data),

  // Update a VO
  update: (projectId, voId, data) =>
    api.patch(`/projects/${projectId}/variation-orders/${voId}`, data),

  // Delete a VO
  delete: (projectId, voId) =>
    api.delete(`/projects/${projectId}/variation-orders/${voId}`),

  // Record a payment
  recordPayment: (projectId, voId, data) =>
    api.post(`/projects/${projectId}/variation-orders/${voId}/payments`, data),

  // Update a payment
  updatePayment: (projectId, voId, paymentId, data) =>
    api.patch(`/projects/${projectId}/variation-orders/${voId}/payments/${paymentId}`, data),

  // Delete a payment
  deletePayment: (projectId, voId, paymentId) =>
    api.delete(`/projects/${projectId}/variation-orders/${voId}/payments/${paymentId}`),

  // Void a VO (soft cancel)
  voidVO: (projectId, voId, reason) =>
    api.post(`/projects/${projectId}/variation-orders/${voId}/void`, { reason })
};

// Payment Notice APIs
export const paymentNoticeAPI = {
  list: (projectId, milestoneId) =>
    api.get(`/projects/${projectId}/payment-milestones/${milestoneId}/notices`),

  issue: (projectId, milestoneId, data) =>
    api.post(`/projects/${projectId}/payment-milestones/${milestoneId}/notices`, data),

  cancel: (projectId, milestoneId, noticeId, reason) =>
    api.patch(
      `/projects/${projectId}/payment-milestones/${milestoneId}/notices/${noticeId}/cancel`,
      { reason }
    )
};

// Payment Deduction APIs
export const paymentDeductionAPI = {
  list: (projectId) =>
    api.get(`/projects/${projectId}/deductions`),

  create: (projectId, data) =>
    api.post(`/projects/${projectId}/deductions`, data),

  update: (projectId, deductionId, data) =>
    api.patch(`/projects/${projectId}/deductions/${deductionId}`, data),

  delete: (projectId, deductionId) =>
    api.delete(`/projects/${projectId}/deductions/${deductionId}`)
};

// Payment Analytics APIs
export const paymentAnalyticsAPI = {
  getSummary: (params = {}) =>
    api.get('/analytics/payments/summary', { params }),

  getTrend: (params = {}) =>
    api.get('/analytics/payments/trend', { params }),

  getBreakdownByMilestoneType: (params = {}) =>
    api.get('/analytics/payments/by-milestone-type', { params }),

  getProjects: (params = {}) =>
    api.get('/analytics/payments/projects', { params })
};

// Site Database APIs
export const siteDatabaseAPI = {
  // Core site database methods
  getAll: (filters = {}) =>
    api.get('/site-database', { params: filters }),
  get: (id) =>
    api.get(`/site-database/${id}`),
  getByProject: (designProjectId) =>
    api.get(`/site-database/by-project/${designProjectId}`),
  updateStatus: (id, siteStatus) =>
    api.patch(`/site-database/${id}/status`, { siteStatus }),
  updateHandoverNotes: (id, handoverNotes) =>
    api.patch(`/site-database/${id}/handover-notes`, { handoverNotes }),
  updateInternalNotes: (id, internalNotes) =>
    api.patch(`/site-database/${id}/internal-notes`, { internalNotes }),
  updateClientFeedback: (id, clientFeedback) =>
    api.patch(`/site-database/${id}/client-feedback`, { clientFeedback }),
  updatePackagePrice: (id, finalPackagePrice) =>
    api.patch(`/site-database/${id}/package-price`, { finalPackagePrice }),
  assignSiteManager: (id, siteManagerId) =>
    api.patch(`/site-database/${id}/site-manager`, { siteManagerId }),
  assignSiteSupervisor: (id, siteSupervisorId, options = {}) =>
    api.patch(`/site-database/${id}/site-supervisor`, {
      siteSupervisorId,
      ...(options.managerHandles ? { managerHandles: true } : {})
    }),
  getAvailableSiteManagers: () =>
    api.get('/site-database/available-site-managers'),
  getAvailableSiteSupervisors: (siteManagerId = null) =>
    api.get('/site-database/available-site-supervisors', { params: { siteManagerId } }),
  createFromProject: (designProjectId, projectType = 'renovation') =>
    api.post(`/site-database/create-from-project/${designProjectId}`, { projectType }),
  createManual: (data) =>
    api.post('/site-database/create-manual', data),

  // Phase templates
  getPhaseTemplates: () =>
    api.get('/site-database/phase-templates'),

  // Phase management
  getPhases: (siteId) =>
    api.get(`/site-database/${siteId}/phases`),
  getPhase: (siteId, phaseId) =>
    api.get(`/site-database/${siteId}/phases/${phaseId}`),
  addPhase: (siteId, phaseData) =>
    api.post(`/site-database/${siteId}/phases`, phaseData),
  updatePhase: (siteId, phaseId, updates) =>
    api.patch(`/site-database/${siteId}/phases/${phaseId}`, updates),
  removePhase: (siteId, phaseId) =>
    api.delete(`/site-database/${siteId}/phases/${phaseId}`),
  reorderPhases: (siteId, phaseOrder) =>
    api.put(`/site-database/${siteId}/phases/reorder`, { phaseOrder }),
  setupPhases: (siteId, phases) =>
    api.post(`/site-database/${siteId}/phases/setup`, { phases }),
  startPhase: (siteId, phaseId) =>
    api.post(`/site-database/${siteId}/phases/${phaseId}/start`),
  completePhase: (siteId, phaseId, data) =>
    api.post(`/site-database/${siteId}/phases/${phaseId}/complete`, data),
  skipPhase: (siteId, phaseId, data) =>
    api.post(`/site-database/${siteId}/phases/${phaseId}/skip`, data),
  updatePhaseProgress: (siteId, phaseId, data) =>
    api.patch(`/site-database/${siteId}/phases/${phaseId}/progress`, data),

  // Task management
  getTasks: (siteId, phaseId) =>
    api.get(`/site-database/${siteId}/phases/${phaseId}/tasks`),
  createTask: (siteId, phaseId, data) =>
    api.post(`/site-database/${siteId}/phases/${phaseId}/tasks`, data),
  completeTask: (siteId, taskId) =>
    api.post(`/site-database/${siteId}/tasks/${taskId}/complete`),
  uncompleteTask: (siteId, taskId) =>
    api.post(`/site-database/${siteId}/tasks/${taskId}/uncomplete`),
  deleteTask: (siteId, taskId) =>
    api.delete(`/site-database/${siteId}/tasks/${taskId}`),

  // History
  getHistory: (siteId, options = {}) =>
    api.get(`/site-database/${siteId}/history`, { params: options }),

  // Attendance
  recordAttendance: (siteId, formData) =>
    api.post(`/site-database/${siteId}/attendance`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  getAttendance: (siteId, options = {}) =>
    api.get(`/site-database/${siteId}/attendance`, { params: options }),
  getTodayAttendanceStatus: (siteId) =>
    api.get(`/site-database/${siteId}/attendance/today`),
  getAttendancePhotoUrl: (siteId, attendanceId, type = 'processed') =>
    `${api.defaults.baseURL}/site-database/${siteId}/attendance/${attendanceId}/photo?type=${type}`,

  // Attendance Tracker (Dashboard)
  getAttendanceTracker: (filters = {}) =>
    api.get('/site-database/attendance-tracker', { params: filters }),
  getAttendanceTrackerSummary: () =>
    api.get('/site-database/attendance-tracker/summary'),

  // Attendance Map (System Admin)
  getActiveCheckIns: () =>
    api.get('/site-database/attendance-map/active-checkins'),
  getMissingCheckIns: () =>
    api.get('/site-database/attendance-map/missing-checkins')
};

// Site Phase Template APIs
export const sitePhaseTemplateAPI = {
  // Get all templates (global + user's own)
  list: (projectType = null) =>
    api.get('/site-phase-templates', { params: { projectType } }),

  // Get single template
  get: (id) =>
    api.get(`/site-phase-templates/${id}`),

  // Create new template
  create: (data) =>
    api.post('/site-phase-templates', data),

  // Update template
  update: (id, data) =>
    api.put(`/site-phase-templates/${id}`, data),

  // Delete template
  delete: (id) =>
    api.delete(`/site-phase-templates/${id}`)
};

// Design Project APIs
export const designProjectAPI = {
  getNextProjectNumber: () =>
    api.get('/design-projects/next-project-number')
};

// Users by role (for wizard dropdowns)
export const usersAPI = {
  getByRoles: (roles, { includeResigned = false } = {}) =>
    api.get('/users/by-role', {
      params: {
        roles: Array.isArray(roles) ? roles.join(',') : roles,
        ...(includeResigned ? { includeResigned: 'true' } : {})
      }
    })
};

export default api;
